import React, { useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
  Box,
  TOAST_VARIANT_WARNING,
  toastShow,
  Panel,
  Flex,
  FlexItem,
  Text,
  TEXT_VARIANT_LIGHT,
  TEXT_VARIANT_BOLD,
  SkeletonLoader,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SpacingVertical,
  SPACING_8,
  IconMessage1,
  IconMessage1Write,
  IconCheckCircle,
  Link,
  LINK_VARIANT_DEFAULT,
  LINK_VARIANT_HIGHLIGHT
} from 'mailup-web-components'
import './BoxCollaboration.scss'
import {
  useRecoilCallback,
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState
} from 'recoil'
import { modalBuyCollaboration } from 'Pages/OverviewMessage/recoil/state/modal'
import {
  collaborationState,
  messagesCollaborationState,
  messageState
} from 'Pages/OverviewMessage/recoil/state/message'
import { getCollaborationLoginDataCallback } from 'Pages/OverviewMessage/recoil/callbacks/callbacks'
import {
  COLLABORATION_PROJECT_STATUS_APPROVED,
  COLLABORATION_PROJECT_STATUS_ARCHIVED,
  COLLABORATION_PROJECT_STATUS_OPEN
} from 'Constants/'
import evalCondition from 'Helpers/evalCondition'
import styled from '@emotion/styled'
import { getLang } from 'mailup-react-helpers'

const BoxCollaboration = () => {
  const { id } = useParams()
  const intl = useIntl()

  const { current: conditions } = useRef({
    collaborationEnabled: evalCondition('uo:COLLABORATIONS'),
    buyCollaboration: evalCondition(
      'NOT co:COLLABORATIONS OR (uo:GEST AND NOT uo:COLLABORATIONS )'
    )
  })
  const { collaborationEnabled, buyCollaboration } = conditions

  const { subject } = useRecoilValue(messageState)

  const setModalBuy = useSetRecoilState(modalBuyCollaboration)
  const setCollaborationState = useSetRecoilState(collaborationState)

  const getCollaborationLoginData = useRecoilCallback(
    getCollaborationLoginDataCallback,
    []
  )

  const collaborationResult = collaborationEnabled
    ? useRecoilValueLoadable(messagesCollaborationState)
    : undefined

  const collaborationInfo =
    collaborationResult && collaborationResult.state === 'hasValue'
      ? collaborationResult.contents.Items.find(
          o => o.ProjectReference === `NL_${id}`
        )
      : undefined

  const handleClickBuy = () => {
    setModalBuy(true)
  }

  const openCollaboration = async hideResolvedThreads => {
    const result = await getCollaborationLoginData(
      id,
      collaborationInfo ? collaborationInfo.IdProject : undefined,
      subject,
      hideResolvedThreads
    )
    if (result) {
      const lang = getLang()
      const loginUrl = `https://${process.env.COLLABORATION_FRONTEND_URL}/login?token=${result.Data.Token}&lang=${lang}`

      window.open(loginUrl, '_blank', 'noopener,noreferrer')
      setTimeout(() => {
        setCollaborationState(prev => [...prev])
      }, 2000)
    } else {
      toastShow(
        intl.formatMessage({
          id: 'Load.ActiveRecipientSummary.CollaborationErrorLoadingData'
        }),
        TOAST_VARIANT_WARNING,
        'collaboration'
      )
    }
  }

  const handleOpenCollaboration = () => openCollaboration(true)
  const handleOpenCollaborationNoThreads = () => openCollaboration(false)

  if (collaborationResult && collaborationResult.state === 'loading') {
    return (
      <>
        <Panel data-mu-test='overview-message-box-collaboration'>
          <Text variant={TEXT_VARIANT_BOLD}>
            {intl.formatMessage({
              id: 'Load.Collaboration.CollaborationColumnTitle'
            })}
          </Text>
          <SpacingVertical variant={SPACING_8} />
          <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
        </Panel>
      </>
    )
  }

  if (collaborationResult && collaborationResult.state === 'hasError') {
    return (
      <>
        <Panel data-mu-test='overview-message-box-collaboration'>
          <Text variant={TEXT_VARIANT_BOLD}>
            {intl.formatMessage({
              id: 'Load.Collaboration.CollaborationColumnTitle'
            })}
          </Text>
          <SpacingVertical variant={SPACING_8} />
          <a onClick={handleOpenCollaboration}>
            <StyledIconMessage />0
          </a>
          <Flex padding='0 0 0 28px'>
            <Text variant={TEXT_VARIANT_LIGHT}>
              {intl.formatMessage({
                id: 'Load.Collaboration.CollaborationColumnTitle'
              })}
            </Text>
          </Flex>
        </Panel>
      </>
    )
  }

  return (
    <>
      <Panel data-mu-test='overview-message-box-collaboration'>
        <Text variant={TEXT_VARIANT_BOLD}>
          {intl.formatMessage({
            id: 'Load.Collaboration.CollaborationColumnTitle'
          })}
        </Text>
        <SpacingVertical variant={SPACING_8} />

        <Flex>
          {buyCollaboration && (
            <Flex columnGap='8px'>
              <Text>
                {intl.formatMessage({ id: 'Load.Collaboration.NotShared' })}
              </Text>

              <Link
                variant={LINK_VARIANT_HIGHLIGHT}
                onClick={handleClickBuy}
                data-mu-test='link-StartCollaboration-toBuy'
              >
                {intl.formatMessage({
                  id: 'Load.Collaboration.StartCollaborationCta'
                })}
              </Link>
            </Flex>
          )}

          {collaborationEnabled && !collaborationInfo && (
            <Flex columnGap='8px'>
              <Text>
                {intl.formatMessage({ id: 'Load.Collaboration.NotShared' })}
              </Text>

              <Link
                variant={LINK_VARIANT_HIGHLIGHT}
                onClick={handleOpenCollaboration}
                data-mu-test='link-StartCollaboration-enabled'
              >
                {intl.formatMessage({
                  id: 'Load.Collaboration.StartCollaborationCta'
                })}
              </Link>
            </Flex>
          )}

          {collaborationEnabled && collaborationInfo && (
            <Flex columnGap='24px' alignItems='baseline'>
              {(collaborationInfo.State === COLLABORATION_PROJECT_STATUS_OPEN ||
                collaborationInfo.State ===
                  COLLABORATION_PROJECT_STATUS_APPROVED) && (
                <>
                  <FlexItem>
                    <Flex columnGap='8px' alignItems='baseline'>
                      <FlexItem>
                        <Flex alignItems='baseline' flexWrap='nowrap'>
                          <Link onClick={handleOpenCollaboration}>
                            <StyledIconMessage bigger />
                          </Link>

                          <Link
                            variant={LINK_VARIANT_HIGHLIGHT}
                            onClick={handleOpenCollaboration}
                          >
                            {intl.formatNumber(collaborationInfo.NumOfComments)}
                          </Link>
                        </Flex>
                      </FlexItem>

                      {(collaborationInfo.State ===
                        COLLABORATION_PROJECT_STATUS_OPEN ||
                        collaborationInfo.State ===
                          COLLABORATION_PROJECT_STATUS_APPROVED) && (
                        <Text htmlElement='span'>
                          {intl.formatMessage({
                            id: 'Load.Collaboration.TotalComments'
                          })}
                        </Text>
                      )}
                    </Flex>
                  </FlexItem>

                  <FlexItem>
                    <Flex columnGap='8px' alignItems='baseline'>
                      <FlexItem>
                        <Flex alignItems='baseline' flexWrap='nowrap'>
                          <Link onClick={handleOpenCollaborationNoThreads}>
                            <StyledIconOpenThread bigger />
                          </Link>
                          <Link
                            variant={LINK_VARIANT_HIGHLIGHT}
                            onClick={handleOpenCollaborationNoThreads}
                          >
                            {intl.formatNumber(
                              collaborationInfo.NumOfUnresolvedThreads
                            )}
                          </Link>
                        </Flex>
                      </FlexItem>

                      <Text htmlElement='span'>
                        {intl.formatMessage({
                          id: 'Load.Collaboration.OpenThreads'
                        })}
                      </Text>
                    </Flex>
                  </FlexItem>

                  <FlexItem>
                    <Flex columnGap='8px' alignItems='baseline'>
                      <FlexItem>
                        <Flex alignItems='baseline' flexWrap='nowrap'>
                          <Link onClick={handleOpenCollaboration}>
                            <StyledIconClosedThread bigger />
                          </Link>
                          <StyledLinkGreen
                            variant={LINK_VARIANT_HIGHLIGHT}
                            onClick={handleOpenCollaboration}
                          >
                            {intl.formatNumber(
                              collaborationInfo.NumOfResolvedThreads
                            )}
                          </StyledLinkGreen>
                        </Flex>
                      </FlexItem>

                      <Text htmlElement='span'>
                        {intl.formatMessage({
                          id: 'Load.Collaboration.ResolvedThreads'
                        })}
                      </Text>
                    </Flex>
                  </FlexItem>
                </>
              )}

              {collaborationInfo.State ===
                COLLABORATION_PROJECT_STATUS_ARCHIVED && (
                <FlexItem>
                  <Flex alignItems='baseline' flexWrap='nowrap'>
                    <Link onClick={handleOpenCollaboration}>
                      <span className='c-box-collaboration__content__archived' />
                    </Link>
                    <StyledLink
                      variant={LINK_VARIANT_DEFAULT}
                      onClick={handleOpenCollaboration}
                    >
                      {intl.formatMessage({
                        id: 'Load.Collaboration.CollaborationArchived'
                      })}
                    </StyledLink>
                  </Flex>
                </FlexItem>
              )}
            </Flex>
          )}
        </Flex>
      </Panel>
    </>
  )
}

export default BoxCollaboration

const StyledIconMessage = styled(IconMessage1)`
  color: var(--java);
  margin-right: 8px;

  > svg {
    width: ${({ bigger }) => (bigger ? '20px' : '18px')};
    height: ${({ bigger }) => (bigger ? '20px' : '18px')};
  }
`
const StyledIconOpenThread = styled(IconMessage1Write)`
  color: var(--java);
  margin-right: 8px;

  > svg {
    width: ${({ bigger }) => (bigger ? '20px' : '18px')};
    height: ${({ bigger }) => (bigger ? '20px' : '18px')};
  }
`
const StyledIconClosedThread = styled(IconCheckCircle)`
  color: var(--emerald);
  margin-right: 8px;

  > svg {
    width: ${({ bigger }) => (bigger ? '20px' : '18px')};
    height: ${({ bigger }) => (bigger ? '20px' : '18px')};
  }
`

const StyledLinkGreen = styled(Link)`
  color: var(--emerald) !important;

  :hover {
    color: var(--blue-chill) !important;
  }
`

const StyledLink = styled(Link)`
  text-decoration: none !important;
  font-weight: 400 !important;
`
